import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';

/** Discriminated union representing what is currently selected on the canvas. */
export type CanvasSelection =
  | { type: 'none' }
  | { type: 'token'; tokenId: string; pageId: string }
  | { type: 'tokens'; tokenIds: string[]; pageId: string }
  | { type: 'drawing'; strokeId: string; pageId: string }
  | { type: 'text'; textId: string; pageId: string }
  | { type: 'note'; noteId: string; pageId: string }
  | { type: 'mapObject'; objectId: string; pageId: string }
  | { type: 'locationLink'; linkId: string; pageId: string };

/** Value provided by SelectionContext. */
export interface SelectionContextValue {
  /** Current selection state */
  selection: CanvasSelection;
  /** Set a new selection — replaces any previous selection */
  select: (sel: CanvasSelection) => void;
  /** Alias for select({ type: 'none' }) */
  clearSelection: () => void;
  /** Ref synced with state — use in canvas render callbacks */
  selectionRef: React.RefObject<CanvasSelection>;
  /** @deprecated Use selection instead */
  selectedTokenId: string | null;
  /** @deprecated Use selection instead */
  selectedTokenIds: ReadonlySet<string>;
}

const NONE_SELECTION: CanvasSelection = { type: 'none' };
const EMPTY_SET: ReadonlySet<string> = new Set<string>();

const SelectionContext = createContext<SelectionContextValue | null>(null);

/** Props for SelectionProvider. */
interface SelectionProviderProps {
  children: ReactNode;
  /** When this changes, selection is automatically cleared (page navigation). */
  outgoingPageId?: string;
}

/** Provider for unified canvas selection. */
export function SelectionProvider({ children, outgoingPageId }: SelectionProviderProps) {
  const [selection, setSelection] = useState<CanvasSelection>(NONE_SELECTION);
  const selectionRef = useRef<CanvasSelection>(selection);

  const select = useCallback((sel: CanvasSelection) => {
    setSelection(sel);
    selectionRef.current = sel;
  }, []);

  const clearSelection = useCallback(() => {
    select(NONE_SELECTION);
  }, [select]);

  // Auto-clear selection on page change
  useEffect(() => {
    clearSelection();
  }, [outgoingPageId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** @deprecated Use selection instead */
  const selectedTokenId = useMemo(() => {
    if (selection.type === 'token') return selection.tokenId;
    return null;
  }, [selection]);

  /** @deprecated Use selection instead */
  const selectedTokenIds = useMemo(() => {
    if (selection.type === 'token') return new Set([selection.tokenId]);
    if (selection.type === 'tokens') return new Set(selection.tokenIds);
    return EMPTY_SET;
  }, [selection]);

  const value = useMemo<SelectionContextValue>(() => ({
    selection,
    select,
    clearSelection,
    selectionRef,
    selectedTokenId,
    selectedTokenIds,
  }), [selection, select, clearSelection, selectedTokenId, selectedTokenIds]);

  return (
    <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>
  );
}

/**
 * Access SelectionContext value. Must be used within a SelectionProvider.
 * @throws Error if used outside SelectionProvider.
 */
export function useSelectionContext(): SelectionContextValue {
  const ctx = useContext(SelectionContext);
  if (!ctx) throw new Error('useSelectionContext must be used within SelectionProvider');
  return ctx;
}
